import { useEffect, useRef, useState } from 'react'
import ChartMenu, { MenuItem, MenuDivider } from './menu/ChartMenu'
import { useIsPhone } from './hooks/useIsPhone'

// Section open/closed state for the context menu (Drawing / Chart type).
const SEC_KEY = 'solcheddar:ctxMenuOpen'
const SEC_DEFAULTS = { drawing: true, chartType: true }
// Header look copied from BotSettingsMenu's .bot-menu-sec-toggle (that CSS
// only exists while the bot menu is mounted, so it's duplicated here).
const SEC_CSS = `
.cm-sec-toggle {
  display: flex; align-items: baseline; width: 100%; min-width: 0;
  white-space: nowrap; overflow: hidden; background: transparent; border: none;
  border-top: 1px solid #1e1e1e; color: #666; font-size: 10px;
  padding: 4px 6px; cursor: pointer; text-align: left;
}
.cm-sec-toggle:hover { color: #aaa; }
`

// Phone: bottom safe-area inset (home bar) in px. Measured with a hidden
// probe each open (cheap; follows rotation). 0 where env() isn't supported.
const safeBottomPx = () => {
  try {
    const d = document.createElement('div')
    d.style.cssText = 'position:fixed;left:0;bottom:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom, 0px)'
    document.body.appendChild(d)
    const v = parseFloat(getComputedStyle(d).paddingBottom) || 0
    d.remove()
    return v
  } catch { return 0 }
}

export default function ChartContextMenu({
  // Positional actions only — the indicator toggles moved to the beaker
  // menu in the chart toolbar (FootprintChartCanvas), which owns their
  // state directly. This menu keeps what needs the click location.
  show, x, y,
  onAddAVWAP, onAddSRLine, onAddTrendline, trendPending, onAddRect, rectPending,
  // Chart type section: footprint/candle + log/linear (moved off the header).
  chartMode, onChartModeChange, logScale, onToggleLogScale, tpoLetters, onToggleTpoLetters,
  volBars, onToggleVolBars,
  onClose,
  currentTokenAddress,
  onTokenAddressChange,
  isWatched, canWatch, onToggleWatchlist,
  // Indicator settings shortcuts (and anything else location-aware callers
  // want): [{ label, onClick }] — rendered after the positional actions.
  extraItems = [],
}) {

  // Open-delay guard. The menu can appear directly under the pointer/finger
  // that opened it — on phone the double-tap's second tap synthesizes a click
  // that lands on whatever item rendered beneath it, and on desktop a fast
  // release can do the same. Stamp the open time and swallow any item
  // activation for a short window so the opening gesture never selects an
  // item; after the window, taps work normally.
  const isPhone = useIsPhone()
  const OPEN_GUARD_MS = 350
  const openedAtRef = useRef(0)
  useEffect(() => {
    if (show) openedAtRef.current = Date.now()
  }, [show])
  // Wraps every item handler: within the guard window the activation is
  // eaten (and the event stopped) instead of firing the action.
  const guard = (fn) => (e) => {
    if (Date.now() - openedAtRef.current < OPEN_GUARD_MS) {
      e?.preventDefault?.(); e?.stopPropagation?.()
      return
    }
    fn(e)
  }

  // Collapsible sections (Drawing / Chart type), same header look as the
  // bot settings menu. Open state persists per section; default open.
  const [secOpen, setSecOpen] = useState(() => {
    try { return { ...SEC_DEFAULTS, ...JSON.parse(localStorage.getItem(SEC_KEY) || '{}') } }
    catch { return { ...SEC_DEFAULTS } }
  })
  const toggleSec = (k) => setSecOpen(p => {
    const n = { ...p, [k]: !p[k] }
    try { localStorage.setItem(SEC_KEY, JSON.stringify(n)) } catch {}
    return n
  })
  // Header toggles never close the menu; guard() keeps the opening gesture
  // from flipping one.
  const secHead = (k, label) => (
    <button type="button" className="cm-sec-toggle" aria-expanded={!!secOpen[k]}
      onClick={guard(() => toggleSec(k))}>
      {secOpen[k] ? '\u25BE' : '\u25B8'} {label}
    </button>
  )

  // Frame, fit, outside-click / Esc: menu/ChartMenu (was a hand-guessed
  // 170x320 clamp here).

  if (!show) return null

  // Phone: open in the LOWER part of the screen (thumb reach). Horizontal stays
  // at the press x. Anchor y at the viewport bottom minus the safe-area inset;
  // useFitToScreen then flips the menu above it (bottom ~8px above the home
  // bar) and clamps top >= 8px; a taller menu scrolls (ChartMenu cm-scroll).
  // Only the menu's anchor moves; x/y props (press point) are untouched.
  const anchorY = isPhone && typeof window !== 'undefined'
    ? Math.max(0, window.innerHeight - safeBottomPx())
    : y

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (text && text.trim()) {
        onTokenAddressChange(text.trim())
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err)
    }
    onClose()
  }

  const handleCopy = async () => {
    if (currentTokenAddress) {
      try {
        await navigator.clipboard.writeText(currentTokenAddress)
      } catch (err) {
        console.error('Failed to copy to clipboard:', err)
      }
    }
    onClose()
  }

  return (
    <ChartMenu anchor={{ x, y: anchorY }} onClose={onClose} stopMouseDown className="cm-list">
      <MenuItem onClick={guard(handleCopy)}>
        📃 Copy Address
      </MenuItem>
      <MenuItem onClick={guard(handlePaste)}>
        📋 Paste Address
      </MenuItem>
      {canWatch && (
        <MenuItem onClick={guard(() => { onToggleWatchlist(); onClose() })}>
          {isWatched ? '★ Remove Favorite' : '☆ Add Favorite'}
        </MenuItem>
      )}
      <MenuDivider horizontal />
      {extraItems.map((it, i) => (
        <MenuItem key={i} onClick={guard(() => { it.onClick(); onClose() })}>
          {it.label}
        </MenuItem>
      ))}
      <style>{SEC_CSS}</style>
      {secHead('drawing', 'Drawing')}
      {secOpen.drawing && (<>
        <MenuItem onClick={guard(() => { onAddSRLine(); onClose() })}>
          + S/R Line
        </MenuItem>
        {/* Two-click placement: the first pick anchors the start, the second
            finishes it. The label tells you which half you're in. */}
        {onAddTrendline && (
          <MenuItem onClick={guard(() => { onAddTrendline(); onClose() })}>
            {trendPending && !rectPending ? '✓ Finish Trendline' : '+ Trendline'}
          </MenuItem>
        )}
        {onAddRect && (
          <MenuItem onClick={guard(() => { onAddRect(); onClose() })}>
            {rectPending ? '✓ Finish Rectangle' : '+ Rectangle'}
          </MenuItem>
        )}
        <MenuItem onClick={guard(() => { onAddAVWAP(); onClose() })}>
          + AVWAP
        </MenuItem>
      </>)}
      {secHead('chartType', 'Chart type')}
      {secOpen.chartType && (<>
        {onChartModeChange && (<>
          <MenuItem onClick={guard(() => { onChartModeChange('footprint'); onClose() })}>
            {chartMode === 'footprint' ? '\u2713' : '\u00A0\u00A0'} Footprint
          </MenuItem>
          <MenuItem onClick={guard(() => { onChartModeChange('candle'); onClose() })}>
            {chartMode === 'candle' ? '\u2713' : '\u00A0\u00A0'} Candle
          </MenuItem>
          <MenuItem onClick={guard(() => { onChartModeChange('tpo'); onClose() })}>
            {chartMode === 'tpo' ? '\u2713' : '\u00A0\u00A0'} TPO
          </MenuItem>
          {chartMode === 'tpo' && onToggleTpoLetters && (
            <MenuItem onClick={guard(() => { onToggleTpoLetters(); onClose() })}>
              {'\u00A0\u00A0'} TPO: {tpoLetters ? 'show blocks' : 'show letters'}
            </MenuItem>
          )}
        </>)}
        {onToggleVolBars && (
          <MenuItem onClick={guard(() => { onToggleVolBars(); onClose() })}>
            {volBars ? '\u2713' : '\u00A0\u00A0'} Volume
          </MenuItem>
        )}
      </>)}
      <MenuDivider horizontal />
      {/* Theme menu is app-level (ThemePicker); opens at this menu's point. */}
      <MenuItem onClick={guard(() => { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent('cheddar:open-theme', { detail: { x, y } })), 0) })}>
        🖌 Theme
      </MenuItem>
    </ChartMenu>
  )
}
